import React, { useState, useRef, useEffect } from 'react';

import {
  getTotals,
  formatNumberAsUSD,
  formatResultsObject
} from '../utils.js';

import { EMPLOYEE_COSTS_ASTERISK, } from '../constants/fragments.js';

const ResultsTable = props => {
  const { results, salary } = props;

  // We need to handle these values using useState and useEffect so we don't end up
  // recalculating them if they don't actually change. We don't want to generate
  // a new array if the results and salary values don't change because that would trigger
  // a re-render on the child components as well which would mess up the focus stuff.
  const [formattedResults, setFormattedResults] = useState(formatResultsObject(results));
  const [totals, setTotals] = useState(getTotals(results));
  useEffect(() => {
    setFormattedResults(formatResultsObject(props.results));
    setTotals(getTotals(props.results));
  }, [props.results, props.salary]); // only update if these values actually change

  return (
    <div className="results-table-container">
      <AnnualCosts formattedResults={formattedResults} totals={totals}/>

      <p className="small-text">{EMPLOYEE_COSTS_ASTERISK}</p>

      <EstimatedTotal salary={salary} totals={totals} />
    </div>
  );
};

////////////////////
// SUB-COMPONENTS //
////////////////////

const AnnualCosts = props => {
  const { formattedResults, totals } = props;

  const {
    UC: totalUniversity,
    employee: totalEmployee
  } = totals;

  const focusRef = useRef(null);

  // focus on the table when the values change
  useEffect(() => {
    if (focusRef.current) {
      focusRef.current.focus();
    }
  }, [props.formattedResults, props.totals]);

  return (
    <table className="results-table">
      <caption
        tabIndex="-1"
        ref={focusRef}
      >
        Estimated Annual Cost of Benefits
      </caption>
      <thead>
        <tr>
          <th scope="col"><span className="hide">Cost category</span></th>
          <th scope="col">University Costs</th>
          <th scope="col">Employee Costs *</th>
        </tr>
      </thead>
      <tbody>
        {
          formattedResults.map(result => (
            <tr key={result.key}>
              <th scope="row">{result.render}</th>
              <td>{result.UC}</td>
              <td>{result.employee}</td>
            </tr>
          ))
        }
      </tbody>
      <tfoot>
        <tr>
          <th scope="row">Estimated Total Cost</th>
          <td>{formatNumberAsUSD(totalUniversity)}</td>
          <td>{formatNumberAsUSD(totalEmployee)}</td>
        </tr>
      </tfoot>
    </table>
  );
};

const EstimatedTotal = props => {
  const { salary, totals } = props;
  const { UC } = totals;
  const totalCompensation = salary + UC;

  const percentOfSalary = ((UC / salary) * 100).toFixed(2);
  const percentOfSalaryString = `${percentOfSalary}%`;


  return (
    <table className="results-table">
      <caption>Estimated Total Compensation (Benefits and Salary)</caption>
      <tbody>
        <tr>
          <td>University Cost of Benefits as a % of Salary</td>
          <td className="total-cell">{percentOfSalaryString}</td>
        </tr>
        <tr>
          <td>University Cost of Benefits</td>
          <td className="total-cell">{formatNumberAsUSD(UC)}</td>
        </tr>
        <tr>
          <td>Total Compensation (Salary + Benefits)</td>
          <td className="total-cell">{formatNumberAsUSD(totalCompensation)}</td>
        </tr>
      </tbody>
    </table>
  );
};

export default ResultsTable;
